import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Send, X, Mail } from "lucide-react";
import { newsletterApi, type NewsletterEdition } from "./api";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

/**
 * Newsletter (#14) — back-office Nexus.
 *
 * Lista de ediciones con su estado (draft / sent), un modal para redactar
 * una edición nueva (asunto + cuerpo HTML + segmentos por coma) y, para las
 * ediciones en draft, un botón Enviar protegido por confirmAction.
 *
 * Las acciones (Nueva edición / Enviar) sólo se muestran si el user tiene
 * newsletter:manage. Ocultar la UI NO es seguridad — el backend valida
 * requirePermission(newsletter:manage) en cada request.
 */

function inputToTags(value: string): string[] {
  return value
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function fmtDate(value: string | null): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function segmentSummary(e: NewsletterEdition): string {
  const cities = e.segmentCities ?? [];
  const genres = e.segmentGenres ?? [];
  if (cities.length === 0 && genres.length === 0) return "Todos los suscriptores";
  const parts: string[] = [];
  if (cities.length) parts.push(cities.join(", "));
  if (genres.length) parts.push(genres.join(", "));
  return parts.join(" · ");
}

export function NewsletterScreen() {
  const qc = useQueryClient();
  const { push } = useToast();
  const canManage = usePermission(PERMISSIONS.NEWSLETTER_MANAGE);

  const [composing, setComposing] = useState(false);

  const listQuery = useQuery({
    queryKey: ["newsletter", "editions"],
    queryFn: () => newsletterApi.listEditions(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["newsletter"] });

  const sendMut = useMutation({
    mutationFn: (id: number) => newsletterApi.sendEdition(id),
    onSuccess: (res) => {
      push({
        kind: "success",
        message: `Edición enviada a ${res.sent} suscriptor${res.sent === 1 ? "" : "es"}${
          res.failed > 0 ? ` (${res.failed} fallida${res.failed === 1 ? "" : "s"})` : ""
        }.`,
      });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo enviar la edición." }),
  });

  const rows = listQuery.data ?? [];

  const isDraft = (e: NewsletterEdition) => e.status === "draft";

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Comunidad · Newsletter</p>
          <h1 className="nx-h1">Ediciones de newsletter</h1>
          <p className="text-sm text-fg-muted mt-1">
            Redacta y envía campañas a los suscriptores con opt-in. Cada edición es un
            envío único: una vez enviada no se puede reenviar (crea una nueva). Sin
            segmentos, la edición va a todos los suscriptores.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => listQuery.refetch()}
            className="nx-btn-secondary"
            disabled={listQuery.isFetching}
          >
            <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
          {canManage && (
            <button onClick={() => setComposing(true)} className="nx-btn-primary">
              <Plus size={14} /> Nueva edición
            </button>
          )}
        </div>
      </header>

      {listQuery.isLoading ? (
        <div className="text-sm text-fg-subtle py-10 text-center">Cargando ediciones…</div>
      ) : listQuery.isError ? (
        <div className="text-sm text-danger py-10 text-center">
          Error al cargar.{" "}
          <button className="underline" onClick={() => listQuery.refetch()}>
            Reintentar
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border-subtle rounded-lg py-12 text-center">
          <Mail size={28} className="mx-auto mb-2 text-fg-subtle" />
          <p className="text-sm text-fg-muted">Todavía no hay ediciones de newsletter.</p>
          {canManage && (
            <button onClick={() => setComposing(true)} className="nx-btn-secondary mt-3">
              <Plus size={14} /> Crear la primera
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border border-border-subtle rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle bg-bg-subtle text-2xs uppercase tracking-wide text-fg-subtle">
                <th className="text-left font-semibold px-3 py-2">Asunto</th>
                <th className="text-left font-semibold px-3 py-2">Estado</th>
                <th className="text-left font-semibold px-3 py-2">Segmento</th>
                <th className="text-right font-semibold px-3 py-2">Destinatarios</th>
                <th className="text-left font-semibold px-3 py-2">Enviada</th>
                <th className="text-right font-semibold px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr
                  key={e.id}
                  className="border-b border-border-subtle last:border-0 hover:bg-bg-hover"
                >
                  <td className="px-3 py-2">
                    <span className="font-medium text-fg">{e.subject}</span>
                    <div className="text-2xs text-fg-subtle">
                      Creada {fmtDate(e.createdAt)}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        "inline-flex text-2xs rounded px-1.5 py-0.5 border " +
                        (isDraft(e)
                          ? "border-border-subtle text-fg-muted"
                          : "border-success text-success")
                      }
                    >
                      {isDraft(e) ? "Borrador" : "Enviada"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-fg-muted max-w-[260px]">
                    {segmentSummary(e)}
                  </td>
                  <td className="px-3 py-2 text-right text-fg-muted text-xs">
                    {e.status === "sent" ? e.recipientCount : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-fg-muted">{fmtDate(e.sentAt)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      {canManage && isDraft(e) && (
                        <button
                          className="nx-btn-secondary"
                          disabled={sendMut.isPending}
                          onClick={async () => {
                            const summary = segmentSummary(e);
                            const ok = await confirmAction({
                              title: "Enviar edición",
                              message: `"${e.subject}" se enviará ahora a: ${summary}. Esta acción es irreversible y no se puede reenviar.`,
                              confirmLabel: "Enviar ahora",
                              tone: "success",
                            });
                            if (ok === true) sendMut.mutate(e.id);
                          }}
                        >
                          <Send size={14} /> Enviar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {composing && canManage && (
        <ComposeEdition
          onClose={() => setComposing(false)}
          onSaved={() => {
            setComposing(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function ComposeEdition({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [citiesInput, setCitiesInput] = useState("");
  const [genresInput, setGenresInput] = useState("");

  const createMut = useMutation({
    mutationFn: () =>
      newsletterApi.createEdition({
        subject: subject.trim(),
        bodyHtml: bodyHtml.trim(),
        segmentCities: inputToTags(citiesInput),
        segmentGenres: inputToTags(genresInput),
      }),
    onSuccess: () => {
      push({ kind: "success", message: "Edición creada (borrador)." });
      onSaved();
    },
    onError: () =>
      push({ kind: "error", message: "No se pudo crear. Revisá asunto y cuerpo." }),
  });

  const canSave = subject.trim().length >= 3 && bodyHtml.trim().length >= 1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10">
      <div className="w-full max-w-2xl rounded-lg bg-bg-base border border-border-subtle shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <h2 className="text-base font-semibold text-fg">Nueva edición de newsletter</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded text-fg-muted hover:bg-bg-subtle hover:text-fg"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-4">
          <Labeled label="Asunto">
            <input
              className="nx-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Asunto del correo"
              maxLength={240}
            />
          </Labeled>

          <Labeled label="Cuerpo (HTML)">
            <textarea
              className="nx-input min-h-[200px] font-mono text-xs"
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              placeholder="<h1>Hola</h1><p>Contenido de la edición…</p>"
            />
            <span className="text-2xs text-fg-subtle">
              Se inyecta dentro del shell editorial de Mixcore (header/footer + baja
              legal). Podés usar HTML básico.
            </span>
          </Labeled>

          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Ciudades (separadas por coma)">
              <input
                className="nx-input"
                value={citiesInput}
                onChange={(e) => setCitiesInput(e.target.value)}
                placeholder="Santiago, Valparaíso"
              />
            </Labeled>
            <Labeled label="Géneros (separados por coma)">
              <input
                className="nx-input"
                value={genresInput}
                onChange={(e) => setGenresInput(e.target.value)}
                placeholder="Techno, House"
              />
            </Labeled>
          </div>
          <p className="text-2xs text-fg-subtle -mt-1">
            Dejá los segmentos vacíos para enviar a todos los suscriptores con opt-in.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-3">
          <button onClick={onClose} className="nx-btn-secondary">
            Cancelar
          </button>
          <button
            onClick={() => createMut.mutate()}
            disabled={!canSave || createMut.isPending}
            className="nx-btn-primary"
          >
            {createMut.isPending ? "Creando…" : "Crear borrador"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-fg-muted">{label}</span>
      {children}
    </label>
  );
}
